/**
 * Galaxy Run Detail View — shell with phase river, tabs, and sub-tab content.
 *
 * Data: fetchRunOverview() for initial load, openRunStream() for live updates.
 * Composes: phaseRiver, drawer, sidebar, + sub-tab modules
 */

import { route, navigate, registerTeardown } from '../../router.js'
import { esc } from '../../shared/rendering.js'
import { safeHttpUrl } from '../../components/urlSafety.js'
import { action } from '../components/action-delegation.js'
import { renderPhaseRiver } from '../components/phaseRiver.js'
import { renderRunSkeleton } from '../components/skeleton.js'
import { renderCopyBtn } from '../components/copyButton.js'
import {
  renderDrawerShell,
  updateDrawer,
  openDrawerOverlay,
  closeDrawer,
  switchDrawerTab,
} from '../components/drawer.js'
import { togSec } from '../components/section.js'
import { fetchRunOverview, fetchStageDetail, fetchRunState } from '../../api/run.js'
import { formatDuration, formatTokens } from '../../types/domain.js'
import { withErrorBoundary } from '../../otel.js'
import type { RunOverview, StageDetail } from '../../types/api.js'
import { renderOverviewTab } from './run/overview.js'
import { renderViewingDeckTab } from './run/viewingDeck.js'
import { renderTimelineTab } from './run/timeline.js'
import { renderTaskTab } from './run/task.js'
import { renderConfigTab } from './run/config.js'

// ─── Route Registration ─────────────────────────────────────────────────────

route('/run/:run_id', (params) => {
  if (params.run_id) {
    void renderRunDetailView(params.run_id)
  }
})

// ─── State ──────────────────────────────────────────────────────────────────

let _currentOverview: RunOverview | null = null
let _runKeydownListener: ((e: KeyboardEvent) => void) | null = null
let _runGen = 0

// ─── View Renderer ──────────────────────────────────────────────────────────

async function renderRunDetailView(runId: string): Promise<void> {
  const gen = ++_runGen
  _currentOverview = null // reset stale state from prior run (SL-US02)
  const app = document.getElementById('app')
  if (!app) {
    return
  }

  document.title = `Run ${runId} \u2014 Grizzly`
  app.innerHTML = renderRunSkeleton()

  try {
    const overview = await withErrorBoundary('api.run.overview', () => fetchRunOverview(runId), {
      'grizzly.web.run_id': runId,
    })
    if (_runGen !== gen) {
      return
    }
    _currentOverview = overview
    renderShell(app, overview)
    wireEvents()
    // preventScroll: see fleet.ts focus() call — keeps sticky topbar pinned.
    app.focus({ preventScroll: true })
  } catch (err) {
    if (_runGen !== gen) {
      return
    }
    app.innerHTML = `<div class="empty">Failed to load run. ${esc(String(err))}</div>`
  }
}

function renderShell(app: HTMLElement, data: RunOverview): string {
  const s = data.summary
  const statusColor = s.status === 'completed' ? 'grn' : s.status === 'failed' ? 'err' : 'cyn'
  const statusLabel =
    s.status === 'completed' ? 'Completed' : s.status === 'failed' ? 'Failed' : 'Running'
  const dur = formatDuration(s.duration_s) ?? ''
  const tokensText =
    s.tokens_in !== null || s.tokens_out !== null
      ? `${formatTokens(s.tokens_in)} in / ${formatTokens(s.tokens_out)} out`
      : ''

  const hcLinks =
    s.honeycomb_trace_url && safeHttpUrl(s.honeycomb_trace_url)
      ? `<div class="hc-links">
        <a href="${esc(safeHttpUrl(s.honeycomb_trace_url))}" target="_blank" rel="noopener" aria-label="Open Traces in Honeycomb" class="hc-link"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wrn)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></a>
        ${s.honeycomb_logs_url && safeHttpUrl(s.honeycomb_logs_url) ? `<a href="${esc(safeHttpUrl(s.honeycomb_logs_url))}" target="_blank" rel="noopener" aria-label="Open Logs in Honeycomb" class="hc-link"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--grn)" stroke-width="1.8" stroke-linecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></a>` : ''}
        ${s.honeycomb_dashboard_url && safeHttpUrl(s.honeycomb_dashboard_url) ? `<a href="${esc(safeHttpUrl(s.honeycomb_dashboard_url))}" target="_blank" rel="noopener" aria-label="Open Dashboard in Honeycomb" class="hc-link"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--cyn)" stroke-width="1.8" stroke-linecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg></a>` : ''}
      </div>`
      : ''

  const identityParts =
    s.work_item_id === s.run_id
      ? [`${esc(s.run_id)} ${renderCopyBtn(s.run_id)}`]
      : [
          `${esc(s.work_item_id)} ${renderCopyBtn(s.work_item_id)}`,
          `${esc(s.run_id)} ${renderCopyBtn(s.run_id)}`,
        ]
  const statusParts = [
    esc(statusLabel),
    s.total_iterations ? `Iter ${s.total_iterations}` : '',
    s.total_turns ? `${s.total_turns} turn${s.total_turns === 1 ? '' : 's'}` : '',
    tokensText ? esc(tokensText) : '',
    dur ? esc(dur) : '',
  ].filter(Boolean)
  const subLine = [...identityParts, ...statusParts].join(' · ')

  const commitBanner = s.commit_hash
    ? `<div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--grn-d);border:1px solid rgba(62,207,160,.15);border-radius:8px;margin-bottom:12px;font-family:var(--mono);font-size:11px">
        <span style="color:var(--grn);font-weight:600">&#10003; Merged</span>
        <span style="color:var(--t2)">${esc(s.commit_hash)}</span>
        ${s.commit_message ? `<span style="color:var(--t1);font-family:var(--ui);font-weight:500">${esc(s.commit_message)}</span>` : ''}
        ${s.target_branch ? `<span style="color:var(--t4);margin-left:auto">&rarr; ${esc(s.target_branch)}</span>` : ''}
      </div>`
    : ''

  const phaseRiverHtml = renderPhaseRiver(data.execution_graph)

  const html = `
    <div style="display:flex;align-items:center;gap:4px;margin-bottom:8px;font-family:var(--mono);font-size:11px">
      <a tabindex="0" ${action('navigate', '/')} style="color:var(--t3);cursor:pointer;text-decoration:none">Fleet</a>
      <span style="color:var(--t4)">&rsaquo;</span>
      <span style="color:var(--t2)">Run detail</span>
    </div>
    <header class="rh">
      <a class="back" tabindex="0" aria-label="Back to Fleet" ${action('navigate', '/')}>&larr;</a>
      <div class="rh-info">
        <h1 class="rh-t">${esc(s.task_title ?? s.work_item_id)}</h1>
        <span class="rh-sub">${subLine}</span>
      </div>
      <span class="pill" style="background:var(--${statusColor}-d);color:var(--${statusColor})">${statusLabel}</span>
      ${hcLinks}
    </header>
    ${phaseRiverHtml}
    <div class="tabs" id="run-tabs">
      <button class="tab active" data-tab="overview">Overview</button>
      <button class="tab" data-tab="deck">Viewing Deck</button>
      <button class="tab" data-tab="timeline">Timeline</button>
      <button class="tab" data-tab="task">Task</button>
      <button class="tab" data-tab="config">Config</button>
    </div>
    ${commitBanner}
    <div class="tc active" id="tab-overview">${renderOverviewTab(data)}</div>
    <div class="tc" id="tab-deck">${renderViewingDeckTab(data, s.run_id)}</div>
    <div class="tc" id="tab-timeline"><div class="empty">Click to load timeline</div></div>
    <div class="tc" id="tab-task"><div class="empty">Click to load task details</div></div>
    <div class="tc" id="tab-config"><div class="empty">Click to load configuration</div></div>
    ${renderDrawerShell()}`

  app.innerHTML = html
  return html
}

// ─── Event Wiring ───────────────────────────────────────────────────────────

function wireEvents(): void {
  const loadedTabs = new Set<string>(['overview', 'deck'])

  const activateTab = (tabName: string): void => {
    const btn = document.querySelector<HTMLElement>(`#run-tabs .tab[data-tab="${tabName}"]`)
    if (!btn) return
    document.querySelectorAll('#run-tabs .tab').forEach((t) => {
      t.classList.remove('active')
    })
    document.querySelectorAll('.tc').forEach((c) => {
      c.classList.remove('active')
    })
    btn.classList.add('active')
    const tabId = `tab-${tabName}`
    document.getElementById(tabId)?.classList.add('active')

    if (!loadedTabs.has(tabName) && _currentOverview) {
      loadedTabs.add(tabName)
      const container = document.getElementById(tabId)
      if (container) {
        const s = _currentOverview.summary
        if (tabName === 'timeline') {
          const stages = [...new Set(_currentOverview.execution_graph.map((n) => n.stage))]
          container.innerHTML = renderTimelineTab(s.run_id, stages)
        } else if (tabName === 'task') {
          container.innerHTML = renderTaskTab(s.work_item_id)
        } else if (tabName === 'config') {
          container.innerHTML = renderConfigTab(s.run_id)
        }
      }
    }
  }

  // Tab switching with lazy loading
  document.querySelectorAll('#run-tabs .tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      activateTab((btn as HTMLElement).dataset.tab ?? '')
    })
  })

  document.querySelectorAll<HTMLElement>('[data-run-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      activateTab(btn.dataset.runTab ?? '')
    })
  })

  // Keyboard tab switching (1-5) — remove prior listener before adding new one (SL-US03)
  if (_runKeydownListener !== null) {
    document.removeEventListener('keydown', _runKeydownListener)
  }
  _runKeydownListener = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return
    }
    if (e.key === 'Escape') {
      closeDrawer()
    }
    const tabs = document.querySelectorAll('#run-tabs .tab')
    if (tabs.length && e.key >= '1' && e.key <= String(tabs.length)) {
      ;(tabs[parseInt(e.key) - 1] as HTMLElement)?.click()
    }
  }
  document.addEventListener('keydown', _runKeydownListener)

  registerTeardown(() => {
    if (_runKeydownListener !== null) {
      document.removeEventListener('keydown', _runKeydownListener)
      _runKeydownListener = null
    }
  })
}

// ─── Expose globals for onclick handlers ────────────────────────────────────

// These window assignments expose handlers for data-action delegation and legacy onclick attributes.
window.openDrawer = async (stageId: string) => {
  if (!_currentOverview) {
    return
  }
  openDrawerOverlay()
  try {
    const detail: StageDetail = await fetchStageDetail(_currentOverview.summary.run_id, stageId)
    const dur = formatDuration(detail.duration_s)
    const tokText =
      detail.tokens_in !== null || detail.tokens_out !== null
        ? `${formatTokens(detail.tokens_in)} in / ${formatTokens(detail.tokens_out)} out`
        : null
    updateDrawer({
      stage: detail.stage,
      loopNumber: detail.loop_number,
      iteration: detail.iteration,
      status: detail.status,
      duration: dur,
      turns: detail.turns,
      tokens: tokText,
      model: detail.model,
      responseSummary: detail.response_summary,
      filesTouched: detail.files_touched.map((f) => ({ path: f.path, op: f.op })),
      kbItemsCreated: [...detail.kb_items_created],
      taskLists:
        detail.task_lists?.map((tl) => ({
          title: tl.title,
          progress: `${tl.completed}/${tl.total} (${tl.total > 0 ? Math.round((tl.completed / tl.total) * 100) : 0}%)`,
          items: tl.items.map((i) => ({
            text: i.text,
            status: i.status,
            duration: formatDuration(i.duration_s),
          })),
        })) ?? null,
      prompt: detail.prompt,
      traceId: detail.trace_id,
      // Backend should add `tool_distribution` to StageDetail:
      // toolDistribution: detail.tool_distribution?.map(t => ({ label: t.label, totalCalls: t.total_calls, totalDuration: formatDuration(t.total_duration_s) ?? '0s', percent: Math.round(t.percent_of_total) })),
    })
  } catch (_err) {
    closeDrawer()
  }
}
window.closeDrawer = closeDrawer
window.togSec = togSec
window.switchDrawerTab = switchDrawerTab

void navigate
void fetchRunState
